import { DOC_CONCURRENT_SNAPSHOT_CACHE_SIZE, DOC_ENTITY_NODE_CACHE_SIZE, type Doc } from "building-types/doc";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { LRUObjectCache } from "common/utils/lruObjectCache";
import { docEntityHandleHash } from "./core/handleHash";
import type { DocEntityNode } from "./docEntityNode";
import type { DocEntityNodeSource } from "./docEntityNodeRegistryTypes";
import ContentTemplateDocEntityNode from "./docEntityNodes/ContentTemplateDocEntityNode";
import DefaultDocEntityNode from "./docEntityNodes/DefaultDocEntityNode";
import { ProfileExtrusionDescriptorFallbackDocEntityNode } from "./docEntityNodes/ProfileExtrusionDescriptorFallbackDocEntityNode";
import { RoomLayoutEntityDocEntityNodeBase } from "./docEntityNodes/RoomLayoutEntityDocEntityNodeBase";
import { StairSystemDocEntityNode } from "./docEntityNodes/StairSystemDocEntityNode";
import type { DocEntityTypeToHandleType } from "./docEntityTypeToHandleType";
import docGetEntity from "./docGetEntity";

type PerDocNodeCache = LRUObjectCache<string, DocEntityNode>;

export class DocEntityNodeRegistry implements DocEntityNodeSource {
  private static docNodeCache = new LRUObjectCache<Doc, PerDocNodeCache>(
    DOC_CONCURRENT_SNAPSHOT_CACHE_SIZE,
    (a, b) => a === b,
    (perDocCache) => perDocCache.clear(),
  );
  private static instance = new DocEntityNodeRegistry();

  private constructor() {}

  // Instance method to match DocEntityNodeSource interface
  getNode(doc: Doc, handle: DocEntityHandle): DocEntityNode | undefined {
    return DocEntityNodeRegistry.getNode(doc, handle);
  }

  static getNode<T extends DocEntityHandle>(
    doc: Doc,
    handle: T,
  ): DocEntityNode<T["type"] & keyof DocEntityTypeToHandleType> | undefined {
    const hash = docEntityHandleHash(handle);

    let nodeCache = this.docNodeCache.get(doc);
    if (!nodeCache) {
      nodeCache = new LRUObjectCache(DOC_ENTITY_NODE_CACHE_SIZE);
      this.docNodeCache.add(doc, nodeCache);
    }

    const cachedNode = nodeCache.get(hash);
    if (cachedNode) {
      return cachedNode;
    }

    const entity = docGetEntity(doc, handle);
    if (!entity) {
      return undefined;
    }

    const node = this.getNodeUncached(doc, handle);
    nodeCache.add(hash, node);
    return node;
  }

  static getNodes<T extends DocEntityHandle>(
    doc: Doc,
    handles: T[],
  ): DocEntityNode<T["type"] & keyof DocEntityTypeToHandleType>[] {
    return handles.map((handle) => this.getNode(doc, handle)).filter((node) => !!node);
  }

  static getSource(): DocEntityNodeSource {
    return DocEntityNodeRegistry.instance;
  }

  static clearDocCache(doc: Doc): void {
    this.docNodeCache.delete(doc);
  }

  static clearAllDocCaches(): void {
    this.docNodeCache.clear();
  }

  private static getNodeUncached(doc: Doc, handle: DocEntityHandle): DocEntityNode {
    switch (handle.type) {
      case "ANNOTATION":
        return new RoomLayoutEntityDocEntityNodeBase<"ANNOTATION">(handle, doc, DocEntityNodeRegistry.instance);
      case "CASEWORK":
        return new RoomLayoutEntityDocEntityNodeBase<"CASEWORK">(handle, doc, DocEntityNodeRegistry.instance);
      case "CONTENT_TEMPLATE":
        return new ContentTemplateDocEntityNode(handle, doc, DocEntityNodeRegistry.instance);
      case "OPENING":
        return new RoomLayoutEntityDocEntityNodeBase<"OPENING">(handle, doc, DocEntityNodeRegistry.instance);
      case "PROP_INSTANCE":
        return new RoomLayoutEntityDocEntityNodeBase<"PROP_INSTANCE">(handle, doc, DocEntityNodeRegistry.instance);
      case "CONTENT_TEMPLATE_INSTANCE":
        return new RoomLayoutEntityDocEntityNodeBase<"CONTENT_TEMPLATE_INSTANCE">(
          handle,
          doc,
          DocEntityNodeRegistry.instance,
        );
      case "STAIR_SYSTEM":
        return new StairSystemDocEntityNode(handle, doc, DocEntityNodeRegistry.instance);
      case "FACADE_SWEEP_STRATUM":
        return new ProfileExtrusionDescriptorFallbackDocEntityNode<"FACADE_SWEEP_STRATUM">(
          handle,
          doc,
          DocEntityNodeRegistry.instance,
        );
      case "ROOF":
        return new ProfileExtrusionDescriptorFallbackDocEntityNode<"ROOF">(handle, doc, DocEntityNodeRegistry.instance);
      case "SETTINGS":
        return new ProfileExtrusionDescriptorFallbackDocEntityNode<"SETTINGS">(
          handle,
          doc,
          DocEntityNodeRegistry.instance,
        );
      case "WALL_PLANE_TRIM":
        return new ProfileExtrusionDescriptorFallbackDocEntityNode<"WALL_PLANE_TRIM">(
          handle,
          doc,
          DocEntityNodeRegistry.instance,
        );
      default:
        return new DefaultDocEntityNode(
          handle as DocEntityTypeToHandleType[typeof handle.type],
          doc,
          DocEntityNodeRegistry.instance,
        );
    }
  }
}
